// Import necessary hooks from React
import React, { useState, useEffect, useRef } from 'react';
// Import Firebase modules
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, collection, query, where, serverTimestamp, getDoc, getDocs } from 'firebase/firestore';

// Global variables provided by the Canvas environment.
// For local development, these might be undefined, so we access them via `window`
// and provide fallback values.
// NOTE: firebaseConfig is now hardcoded directly in this file to ensure initialization.
const appId = 'behappyradio-37559';
//const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id';
// The initialAuthToken is not directly used for sign-in in this version,
// as we rely on onAuthStateChanged and signInAnonymously for initial auth.
// const initialAuthToken = typeof window.__initial_auth_token !== 'undefined' ? window.__initial_auth_token : null;

// Hardcoded Firebase configuration based on your provided details
const firebaseConfig = {
  apiKey: "AIzaSyCVB8hQtHVuXOy9ug3ahT_ouGUwciULg5E",
  authDomain: "behappyradio-37559.firebaseapp.com",
  projectId: "behappyradio-37559", // <-- Αυτό είναι το Project ID
  storageBucket: "behappyradio-37559.firebasestorage.app",
  messagingSenderId: "690315094745", // <-- Αυτό είναι το Messaging Sender ID
  appId: "1:690315094745:web:b516246d8beedd4e722cc8",
  measurementId: "G-EMND6YCP2P"
};

// Log the appId being used for debugging purposes
console.log("Using appId:", appId);

let app;
let db;
let auth;
let firebaseInitialized = false; // New flag to track successful Firebase initialization

try {
  // Initialize Firebase directly with the hardcoded config
  if (firebaseConfig && firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    auth = getAuth(app);
    firebaseInitialized = true; // Set flag to true if initialization is successful
    console.log("Firebase initialized successfully with hardcoded config.");
  } else {
    console.error("Firebase initialization skipped: 'projectId' not found in hardcoded config.");
  }
} catch (error) {
  console.error("Firebase initialization error during setup:", error);
}

// Custom Modal Component for messages (e.g., errors, confirmations)
const MessageModal = ({ message, onClose }) => {
  if (!message) return null; // If there's no message, don't display the modal

  return (
    // The overlay that covers the entire screen with a dark, translucent background
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      {/* The message box */}
      <div className="bg-gray-800 p-8 rounded-lg shadow-xl border border-purple-700 max-w-sm w-full text-center">
        <p className="text-white text-lg mb-6">{message}</p>
        <button
          onClick={onClose} // Closes the modal when clicked
          className="px-6 py-3 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105"
        >
          OK
        </button>
      </div>
    </div>
  );
};

// Component for User Login and Registration (now as a Modal)
// Added onGuestLogin prop to handle explicit guest login
const AuthComponent = ({ onLogin, setModalMessage, onClose, onGuestLogin }) => {
  const [identifier, setIdentifier] = useState(''); // For login: username or email
  const [password, setPassword] = useState('');
  const [usernameForRegistration, setUsernameForRegistration] = useState(''); // For registration: actual username
  const [emailForRegistration, setEmailForRegistration] = useState(''); // For registration: actual email
  const [isRegistering, setIsRegistering] = useState(false);
  const [authError, setAuthError] = useState(''); // State for authentication-specific errors
  const [showForgotPassword, setShowForgotPassword] = useState(false); // State to show forgot password form
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState(''); // State for forgot password email

  // Handle email/password authentication (login or registration)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError(''); // Clear previous errors


    console.log("handleSubmit called.");
    console.log("Firebase Initialized Status:", firebaseInitialized);
    console.log("App ID at handleSubmit start:", appId);



    // Ensure Firebase is initialized before attempting auth operations
    if (!firebaseInitialized || !auth || !db) {
      setAuthError('Η σύνδεση δεν είναι διαθέσιμη. Παρακαλώ δοκιμάστε ξανά αργότερα ή επικοινωνήστε με τον διαχειριστή.');
      return;
    }

    try {
      if (isRegistering) {
        // Registration
        // Username is now optional. Only email and password are required.
        if (!emailForRegistration || !password) {
          setAuthError('Παρακαλώ συμπληρώστε τα πεδία email και κωδικού.');
          return;
        }
        // Check if username already exists in Firestore (optional, but good practice)
        // Only check if username is provided
        if (usernameForRegistration) {
          const usernameQuery = query(collection(db, `artifacts/${appId}/public/data/userProfiles`), where("username", "==", usernameForRegistration));
          const usernameSnapshot = await getDocs(usernameQuery);
          if (!usernameSnapshot.empty) {
            setAuthError('Το όνομα χρήστη υπάρχει ήδη. Παρακαλώ επιλέξτε άλλο.');
            return;
          }
        }


        const userCredential = await createUserWithEmailAndPassword(auth, emailForRegistration, password);
        const user = userCredential.user;
        console.log("App ID used for Firestore:", appId); // <-- Console.log added here
        console.log("User UID for Firestore:", user.uid); // <-- Console.log added here
        await setDoc(doc(db, `artifacts/${appId}/public/data/userProfiles/${user.uid}`), {
          username: usernameForRegistration, // This will be an empty string if not provided
          email: emailForRegistration,
          createdAt: serverTimestamp(),
        });
        onLogin({ username: usernameForRegistration || emailForRegistration, isOwner: false, uid: user.uid }); // Use email as fallback for username
        setModalMessage('Η εγγραφή ολοκληρώθηκε και συνδεθήκατε!');
        onClose();
      } else {
        // Login
        // Hardcoded owner login
        if (identifier === 'owner' && password === 'owner@1234') {
          onLogin({ username: 'owner', isOwner: true, uid: 'owner-uid' });
          onClose();
          return;
        }

        let actualEmail = identifier; // Assume identifier is email first
        let userCredential;

        try {
          userCredential = await signInWithEmailAndPassword(auth, actualEmail, password);
        } catch (error) {
          // If login with email fails, try to find email by username
          if (error.code === 'auth/invalid-email' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
            const usernameQuery = query(collection(db, `artifacts/${appId}/public/data/userProfiles`), where("username", "==", identifier));
            const usernameSnapshot = await getDocs(usernameQuery);

            if (!usernameSnapshot.empty) {
              const userProfileData = usernameSnapshot.docs[0].data();
              actualEmail = userProfileData.email;
              userCredential = await signInWithEmailAndPassword(auth, actualEmail, password); // Second attempt with found email
            } else {
              throw error; // If not found by username either, re-throw original error
            }
          } else {
            throw error; // Re-throw other Firebase errors
          }
        }

        const user = userCredential.user;
        const userProfileDoc = await getDoc(doc(db, `artifacts/${appId}/public/data/userProfiles/${user.uid}`));
        const username = userProfileDoc.exists() ? userProfileDoc.data().username : user.email; // Fallback to email if username not found
        onLogin({ username: username, isOwner: false, uid: user.uid });
        setModalMessage('Συνδεθήκατε επιτυχώς!');
        onClose();
      }
    } catch (error) {
      console.error("Firebase Auth Error:", error);
      console.error("Firebase Auth Error Code:", error.code); // Added for detailed debugging
      console.error("Firebase Auth Error Message:", error.message); // Added for detailed debugging
      let errorMessage = 'Προέκυψε σφάλμα. Παρακαλώ δοκιμάστε ξανά.';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Αυτό το email χρησιμοποιείται ήδη.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Μη έγκυρη διεύθυνση email.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Ο κωδικός πρόσβασης πρέπει να είναι τουλάχιστον 6 χαρακτήρες.';
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Λάθος email ή κωδικός.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Πρόβλημα σύνδεσης στο διαδίκτυο. Ελέγξτε τη σύνδεσή σας.';
      }
      setAuthError(errorMessage); // Set auth-specific error
    }
  };

  // Handle forgot password submission
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setAuthError(''); // Clear previous errors

    if (!firebaseInitialized || !auth) {
      setAuthError('Η λειτουργία επαναφοράς κωδικού δεν είναι διαθέσιμη.');
      return;
    }
    if (!forgotPasswordEmail) {
      setAuthError('Παρακαλώ εισάγετε το email σας.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setModalMessage('Έχει σταλεί email επαναφοράς κωδικού. Παρακαλώ ελέγξτε τα εισερχόμενά σας.');
      setShowForgotPassword(false); // Hide forgot password form
      setForgotPasswordEmail(''); // Clear email field
      onClose(); // Close the auth modal
    } catch (error) {
      console.error("Forgot Password Error:", error);
      console.error("Forgot Password Error Code:", error.code); // Added for detailed debugging
      console.error("Forgot Password Error Message:", error.message); // Added for detailed debugging
      let errorMessage = 'Προέκυψε σφάλμα κατά την αποστολή του email επαναφοράς κωδικού.';
      if (error.code === 'auth/invalid-email') {
        errorMessage = 'Μη έγκυρη διεύθυνση email.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'Δεν βρέθηκε χρήστης με αυτό το email.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Πρόβλημα σύνδεσης στο διαδίκτυο. Ελέγξτε τη σύνδεσή σας.';
      }
      setAuthError(errorMessage);
    }
  };

  return (
    // The modal overlay
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      {/* The message box */}
      <div className="bg-gray-800 p-8 rounded-xl shadow-2xl border-2 border-purple-700 w-full max-w-md text-center relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl font-bold"
          aria-label="Κλείσιμο"
        >
          &times;
        </button>
        <h2 className="text-4xl font-extrabold text-purple-400 mb-8 animate-pulse">
          {showForgotPassword ? 'Ξέχασα τον Κωδικό μου' : (isRegistering ? 'Εγγραφή στο BeHappyRadio' : 'Σύνδεση στο BeHappyRadio')}
        </h2>

        {authError && (
          <div className="bg-red-800 text-red-200 p-3 rounded-md mb-4 border border-red-600">
            {authError}
          </div>
        )}

        {showForgotPassword ? (
          <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
            <div>
              <label htmlFor="forgotPasswordEmail" className="block text-purple-300 text-lg font-semibold mb-2">Email</label>
              <input
                type="email"
                id="forgotPasswordEmail"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="Εισάγετε το email σας"
                required
                autoComplete="email"
              />
            </div>
            <button
              type="submit"
              className="w-full px-6 py-3 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105"
            >
              Αποστολή Email Επαναφοράς
            </button>
            <button
              type="button"
              onClick={() => { setShowForgotPassword(false); setAuthError(''); }}
              className="w-full px-6 py-3 bg-gray-700 text-purple-300 font-bold rounded-full shadow-lg hover:bg-gray-600 transition duration-300 transform hover:scale-105 mt-4"
            >
              Πίσω στη Σύνδεση
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {isRegistering ? (
              <>
                
                <div>
                  <label htmlFor="emailForRegistration" className="block text-purple-300 text-lg font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    id="emailForRegistration"
                    value={emailForRegistration}
                    onChange={(e) => setEmailForRegistration(e.target.value)}
                    className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
                    placeholder="Εισάγετε το email σας"
                    required
                    autoComplete="email"
                  />
                </div>
              </>
            ) : (
              <div>
                <label htmlFor="identifier" className="block text-purple-300 text-lg font-semibold mb-2">Email</label>
                <input
                  type="text"
                  id="identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
                  placeholder="Εισάγετε email"
                  required
                  autoComplete="username" // Use username for general autocomplete
                />
              </div>
            )}
            <div>
              <label htmlFor="password" className="block text-purple-300 text-lg font-semibold mb-2">Κωδικός</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="Εισάγετε κωδικό"
                required
                autoComplete={isRegistering ? "new-password" : "current-password"}
              />
            </div>
            <button
              type="submit"
              className="w-full px-6 py-3 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105"
            >
              {isRegistering ? 'Εγγραφή' : 'Σύνδεση'}
            </button>
            <button
              type="button"
              onClick={() => setIsRegistering(!isRegistering)}
              className="w-full px-6 py-3 bg-gray-700 text-purple-300 font-bold rounded-full shadow-lg hover:bg-gray-600 transition duration-300 transform hover:scale-105 mt-4"
            >
              {isRegistering ? 'Έχετε ήδη λογαριασμό; Συνδεθείτε' : 'Νέος χρήστης; Εγγραφείτε εδώ'}
            </button>
            {/* New button for Guest Login */}
            {!isRegistering && ( // Only show guest login button when not in registration mode
              <>
               
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setAuthError(''); }}
                  className="w-full text-purple-300 hover:text-purple-400 text-sm mt-2"
                >
                  Ξέχασα τον κωδικό μου;
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

// "About Us" Component
const AboutUsComponent = () => (
  <div className="p-6 bg-gray-800 rounded-lg shadow-lg max-w-3xl mx-auto">
    <h2 className="text-4xl font-bold text-purple-400 mb-6 text-center">Σχετικά με το BeHappyRadio</h2>
    <p className="text-gray-300 text-lg leading-relaxed mb-4">
      Καλώς ήρθατε στο BeHappyRadio, τον απόλυτο προορισμό σας για ανεβαστικές μελωδίες και ενδιαφέρον περιεχόμενο! Είμαστε αφοσιωμένοι στο να σας προσφέρουμε την καλύτερη μουσική, διορατικά άρθρα και διασκεδαστικά βίντεο για να φωτίσουμε τη μέρα σας.
    </p>
    <p className="text-gray-300 text-lg leading-relaxed mb-4">
      Η αποστολή μας είναι να δημιουργήσουμε μια κοινότητα όπου η ευτυχία είναι μεταδοτική και οι καλές δονήσεις είναι πάντα στον αέρα. Είτε είστε εδώ για να ακούσετε τη ζωντανή μας ροή, να ενημερωθείτε για τα τελευταία άρθρα, είτε να ανακαλύψετε νέα βίντεο, ελπίζουμε να βρείτε χαρά και έμπνευση.
    </p>
    <p className="text-gray-300 text-lg leading-relaxed">
      Σας ευχαριστούμε που είστε μέρος της οικογένειας του BeHappyRadio!
    </p>
  </div>
);

// "Contact Us" Component
const ContactUsComponent = ({ setModalMessage }) => {
  const [name, setName] = useState(''); // State for name
  const [email, setEmail] = useState(''); // State for email
  const [message, setMessage] = useState(''); // State for message
  const [isSubmitted, setIsSubmitted] = useState(false); // State for whether the form has been submitted

  // Contact form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate submission (in a real app, data would be sent to a server)
    console.log({ name, email, message });
    setIsSubmitted(true);
    setModalMessage('Ευχαριστούμε για το μήνυμά σας! Θα επικοινωνήσουμε μαζί σας σύντομα.');
    setName(''); // Clear fields
    setEmail('');
    setMessage('');
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-lg max-w-2xl mx-auto">
      <h2 className="text-4xl font-bold text-purple-400 mb-6 text-center">Επικοινωνία</h2>
      {isSubmitted ? (
        <p className="text-green-400 text-xl text-center">Το μήνυμά σας έχει σταλεί!</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-purple-300 text-lg font-semibold mb-2">Το Όνομά σας</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="Εισάγετε το όνομά σας"
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-purple-300 text-lg font-semibold mb-2">Το Email σας</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="Εισάγετε το email σας"
              required
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-purple-300 text-lg font-semibold mb-2">Μήνυμα</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="5"
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none resize-y"
              placeholder="Πληκτρολογήστε το μήνυμά σας εδώ..."
              required
            ></textarea>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-3 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105"
            >
              Αποστολή Μηνύματος
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Chat Component
const ChatComponent = ({ currentUser, setModalMessage, setShowAuthModal, isOwner }) => {
  const [chatMessages, setChatMessages] = useState(() => {
    // Load messages from localStorage on initial load
    const savedChatMessages = localStorage.getItem('chatMessages');
    return savedChatMessages ? JSON.parse(savedChatMessages) : [];
  });
  const [newChatMessage, setNewChatMessage] = useState('');
  // Ref for the scrollable chat messages container
  const chatMessagesContainerRef = useRef(null);
  // New state for muted users: { uid: muteEndTimeTimestamp }
  const [mutedUsers, setMutedUsers] = useState(() => {
    const savedMutedUsers = localStorage.getItem('mutedUsers');
    return savedMutedUsers ? JSON.parse(savedMutedUsers) : {};
  });

  // Effect to save messages to localStorage when they change
  useEffect(() => {
    localStorage.setItem('chatMessages', JSON.stringify(chatMessages));
  }, [chatMessages]);

  // Effect to save muted users to localStorage
  useEffect(() => {
    localStorage.setItem('mutedUsers', JSON.stringify(mutedUsers));
  }, [mutedUsers]);

  // Effect to scroll to the bottom when chatMessages update (e.g., new message from other users)
  // This will NOT trigger when you are typing, only when the chatMessages array changes.
  useEffect(() => {
    if (chatMessagesContainerRef.current) {
      chatMessagesContainerRef.current.scrollTop = chatMessagesContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);


  // Effect to check and unmute users
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const updatedMutedUsers = { ...mutedUsers };
      let changed = false;
      for (const uid in updatedMutedUsers) {
        if (updatedMutedUsers[uid] <= now) {
          delete updatedMutedUsers[uid];
          changed = true;
        }
      }
      if (changed) {
        setMutedUsers(updatedMutedUsers);
      }
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [mutedUsers]);

  const handleSendMessage = (e) => {
    e.preventDefault(); // Prevent default form submission behavior (page reload/scroll)

    // Check if the current user is NOT an owner and is a 'Guest' or null
    if (!currentUser || currentUser.username === 'Guest') {
      setShowAuthModal(true); // Display login modal if user is not logged in or is a guest
      setModalMessage('Πρέπει να συνδεθείτε για να γράψετε στο chat.');
      return;
    }

    // Check if the current user is muted
    if (currentUser.uid && mutedUsers[currentUser.uid] && mutedUsers[currentUser.uid] > Date.now()) {
      const remainingTime = Math.ceil((mutedUsers[currentUser.uid] - Date.now()) / 1000);
      setModalMessage(`Έχετε σιγήσει για ${remainingTime} δευτερόλεπτα. Δεν μπορείτε να γράψετε στο chat.`);
      return;
    }

    if (newChatMessage.trim() === '') {
      setModalMessage('Το μήνυμα δεν μπορεί να είναι κενό.');
      return;
    }

    const newMessageObj = {
      id: Date.now(), // Unique ID for deletion
      user: currentUser.username,
      uid: currentUser.uid, // Store UID for muting
      text: newChatMessage.trim(),
      timestamp: new Date().toLocaleTimeString(),
    };

    // Update chat messages and then scroll to bottom
    setChatMessages((prevMessages) => {
      const updatedMessages = [...prevMessages, newMessageObj];
      // Use setTimeout to ensure DOM has updated before attempting to scroll
      setTimeout(() => {
        if (chatMessagesContainerRef.current) {
          chatMessagesContainerRef.current.scrollTop = chatMessagesContainerRef.current.scrollHeight;
        }
      }, 0);
      return updatedMessages;
    });
    setNewChatMessage('');
  };

  const handleChatInputFocus = () => {
    // Check if the current user is NOT an owner and is a 'Guest' or null
    if (!currentUser || currentUser.username === 'Guest') {
      setShowAuthModal(true);
      setModalMessage('Πρέπει να συνδεθείτε για να γράψετε στο chat.');
    }
  };

  // Function to delete a message (owner only)
  const handleDeleteChatMessage = (id) => {
    if (window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το μήνυμα;')) { // Using window.confirm for simplicity as per instructions
      setChatMessages(prevMessages => prevMessages.filter(msg => msg.id !== id));
      setModalMessage('Το μήνυμα διαγράφηκε επιτυχώς.');
    }
  };

  // Function to mute a user (owner only)
  const handleMuteUser = (uid, username) => {
    const muteDurationMinutes = 5; // Mute for 5 minutes
    const muteEndTime = Date.now() + (muteDurationMinutes * 60 * 1000);
    setMutedUsers(prevMutedUsers => ({
      ...prevMutedUsers,
      [uid]: muteEndTime
    }));
    setModalMessage(`${username} τέθηκε σε σίγαση για ${muteDurationMinutes} λεπτά.`);
  };

  // Function to unmute a user (owner only)
  const handleUnmuteUser = (uid, username) => {
    setMutedUsers(prevMutedUsers => {
      const updatedMutedUsers = { ...prevMutedUsers };
      delete updatedMutedUsers[uid];
      return updatedMutedUsers;
    });
    setModalMessage(`${username} απεσύρθηκε από τη σίγαση.`);
  };

  return (
    // The chat box now has max-w-md and h-[500px] to match the player
    <div className="bg-gray-800 p-6 rounded-xl shadow-2xl border-2 border-purple-700 w-full md:flex-1 md:max-w-md flex flex-col h-[500px]">
      <h3 className="text-3xl font-bold text-purple-400 mb-4 text-center">Συνομιλία</h3> {/* Title change */}
      <div ref={chatMessagesContainerRef} className="flex-grow overflow-y-auto pr-2 mb-4 custom-scrollbar">
        {chatMessages.length === 0 ? (
          <p className="text-gray-400 text-center mt-4">Δεν υπάρχουν μηνύματα ακόμα. Ξεκινήστε τη συζήτηση!</p>
        ) : (
          chatMessages.map((msg, index) => (
            <div
              key={msg.id || index} // Use msg.id if available, fallback to index
              className={`mb-2 p-3 rounded-lg break-words flex items-center justify-between ${
                msg.user === 'owner' ? 'bg-orange-600 text-white' : // Owner messages are orange
                (mutedUsers[msg.uid] && mutedUsers[msg.uid] > Date.now() ? 'bg-red-800 text-red-100 opacity-70' : // Muted user messages are red and slightly transparent
                'bg-gray-700 text-white') // Regular user messages
              }`}
            >
              <div>
                <span className="font-semibold">{msg.user}</span>
                <span className="text-gray-400 text-sm ml-2">({msg.timestamp}):</span>
                <p className="mt-1">{msg.text}</p>
                {mutedUsers[msg.uid] && mutedUsers[msg.uid] > Date.now() && (
                  <span className="text-red-300 text-xs mt-1 block">
                    (Σε σίγαση για {Math.ceil((mutedUsers[msg.uid] - Date.now()) / 1000)} δευτ.)
                  </span>
                )}
              </div>
              {isOwner && (
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleDeleteChatMessage(msg.id)}
                    className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition duration-200 transform hover:scale-105"
                    aria-label="Διαγραφή μηνύματος"
                    title="Διαγραφή μηνύματος"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {msg.uid && msg.uid !== 'owner-uid' && ( // Cannot mute owner or users without UID
                    mutedUsers[msg.uid] && mutedUsers[msg.uid] > Date.now() ? (
                      <button
                        onClick={() => handleUnmuteUser(msg.uid, msg.user)}
                        className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition duration-200 transform hover:scale-105"
                        aria-label="Απεσίγαση χρήστη"
                        title="Απεσίγαση χρήστη"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.003A1 1 0 0110 3a1 1 0 01.617.003L16 5.382V10a1 1 0 01-1 1H5a1 1 0 01-1-1V5.382l5.383-2.379zM4 12a1 1 0 011-1h10a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm2 1a1 1 0 100 2h6a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleMuteUser(msg.uid, msg.user)}
                        className="p-2 bg-yellow-600 text-white rounded-full hover:bg-yellow-700 transition duration-200 transform hover:scale-105"
                        aria-label="Σίγαση χρήστη"
                        title="Σίγαση χρήστη"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.003A1 1 0 0110 3a1 1 0 01.617.003L16 5.382V10a1 1 0 01-1 1H5a1 1 0 01-1-1V5.382l5.383-2.379zM4 12a1 1 0 011-1h10a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm2 1a1 1 0 100 2h6a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <form onSubmit={handleSendMessage} className="flex space-x-3">
        <input
          type="text"
          value={newChatMessage}
          onChange={(e) => setNewChatMessage(e.target.value)}
          onFocus={handleChatInputFocus}
          placeholder={currentUser && currentUser.username !== 'Guest' ? "Γράψτε το μήνυμά σας..." : "Συνδεθείτε για να γράψετε..."}
          className={`flex-grow p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none ${!currentUser || currentUser.username === 'Guest' || (currentUser.uid && mutedUsers[currentUser.uid] && mutedUsers[currentUser.uid] > Date.now()) ? 'opacity-60 cursor-not-allowed' : ''}`}
          disabled={!currentUser || currentUser.username === 'Guest' || (currentUser.uid && mutedUsers[currentUser.uid] && mutedUsers[currentUser.uid] > Date.now())}
        />
        <button
          type="submit"
          className="px-6 py-3 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105"
          disabled={!currentUser || currentUser.username === 'Guest' || (currentUser.uid && mutedUsers[currentUser.uid] && mutedUsers[currentUser.uid] > Date.now())}
        >
          Αποστολή
        </button>
      </form>
      {isOwner && (
        <div className="mt-4 p-4 bg-gray-700 rounded-lg border border-purple-600">
          <h4 className="text-xl font-bold text-purple-300 mb-3">Διαχείριση Chat (Ιδιοκτήτης)</h4>
          {Object.keys(mutedUsers).length === 0 ? (
            <p className="text-gray-400 text-sm">Δεν υπάρχουν χρήστες σε σίγαση.</p>
          ) : (
            <div>
              <p className="text-gray-400 text-sm mb-2">Χρήστες σε σίγαση:</p>
              <ul className="list-disc list-inside text-gray-300">
                {Object.keys(mutedUsers).map(uid => {
                  const muteEndTime = mutedUsers[uid];
                  const remainingSeconds = Math.ceil((muteEndTime - Date.now()) / 1000);
                  const username = chatMessages.find(msg => msg.uid === uid)?.user || 'Άγνωστος';
                  return (
                    <li key={uid} className="flex justify-between items-center mb-1">
                      <span>{username} ({uid.substring(0, 6)}...): {remainingSeconds > 0 ? `${remainingSeconds} δευτ. απομένουν` : 'Λήξη'}</span>
                      <button
                        onClick={() => handleUnmuteUser(uid, username)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-full hover:bg-green-700 transition duration-200"
                      >
                        Απεσίγαση
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// The main application component (App Component)
const App = () => {
  // State for the current view (e.g., 'radio', 'articles', 'youtube', 'dashboard' etc.)
  const [currentView, setCurrentView] = useState('radio');
  // State for articles: array of objects { id, title, content, imageUrl }
  const [articles, setArticles] = useState(() => {
    // Load articles from localStorage on initial load
    const savedArticles = localStorage.getItem('articles');
    return savedArticles ? JSON.parse(savedArticles) : [];
  });
  // State for YouTube videos: array of objects { id, videoId, title }
  const [youtubeVideos, setYoutubeVideos] = useState(() => {
    // Load YouTube videos from localStorage
    const savedVideos = localStorage.getItem('youtubeVideos');
    return savedVideos ? JSON.parse(savedVideos) : [];
  });
  // State for the article being edited (null for new article)
  const [editingArticle, setEditingArticle] = useState(null);
  // States for article title, content, and image URL (for new articles or editing)
  const [currentArticleTitle, setCurrentArticleTitle] = useState('');
  const [currentArticleContent, setCurrentArticleContent] = useState('');
  const [currentArticleImageUrl, setCurrentArticleImageUrl] = useState(''); // New state for image URL

  // State for the current logged-in user: { username: string, isOwner: boolean, uid: string } or null
  const [currentUser, setCurrentUser] = useState(null); // Initialize as null

  // Derived states for convenience
  const isLoggedIn = !!currentUser; // Is the user logged in?
  const isOwner = currentUser ? currentUser.isOwner : false; // Is the user an owner?

  // State for the radio stream URL
  const [radioStreamUrl, setRadioStreamUrl] = useState(() => {
    const savedStreamUrl = localStorage.getItem('radioStreamUrl');
    // Default URL for the provided stream
    return savedStreamUrl || 'https://radio.webhosting4u.gr/stream/behappyradio'; // Updated to the new stream URL
  });

  // State for the real number of connected listeners (from Firestore)
  const [connectedListeners, setConnectedListeners] = useState(0);
  // State to track if Firebase auth is ready
  const [isAuthReady, setIsAuthReady] = useState(false);
  // State for the current user's Firestore ID
  const [firestoreUserId, setFirestoreUserId] = useState('');


  // State for modal message
  const [modalMessage, setModalMessage] = useState('');
  // State for displaying the login/registration modal
  const [showAuthModal, setShowAuthModal] = useState(false);

  // States for the title and album art URL of the currently playing song
  const [currentPlayingSongTitle, setCurrentPlayingSongTitle] = useState(() => {
    const savedTitle = localStorage.getItem('currentPlayingSongTitle');
    return savedTitle || 'BeHappyRadio'; // Default title if not set
  });
  const [currentPlayingAlbumArtUrl, setCurrentPlayingAlbumArtUrl] = useState(() => {
    const savedArtUrl = localStorage.getItem('currentPlayingAlbumArtUrl');
    return savedArtUrl || 'https://placehold.co/150x150/6B46C1/FFFFFF?text=BeHappyRadio'; // Default image if not set
  });

  // Effect to save articles to localStorage when they change
  useEffect(() => {
    localStorage.setItem('articles', JSON.stringify(articles));
  }, [articles]);

  // Effect to save YouTube videos to localStorage when they change
  useEffect(() => {
    localStorage.setItem('youtubeVideos', JSON.stringify(youtubeVideos));
  }, [youtubeVideos]);

  // Effect to save the current user to localStorage (only username and isOwner)
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('currentUser', JSON.stringify({ username: currentUser.username, isOwner: currentUser.isOwner }));
    } else {
      localStorage.removeItem('currentUser');
    }
  }, [currentUser]);

  // Effect to save the radio stream URL to localStorage
  useEffect(() => {
    localStorage.setItem('radioStreamUrl', radioStreamUrl);
  }, [radioStreamUrl]);

  // Effect to save song title and album art URL
  useEffect(() => {
    localStorage.setItem('currentPlayingSongTitle', currentPlayingSongTitle);
  }, [currentPlayingSongTitle]);

  useEffect(() => {
    localStorage.setItem('currentPlayingAlbumArtUrl', currentPlayingAlbumArtUrl);
  }, [currentPlayingAlbumArtUrl]);

  // Firebase Initialization and Auth State Listener
  useEffect(() => {
    // Only proceed if Firebase was successfully initialized
    if (!firebaseInitialized || !app || !auth || !db) {
      console.log("Firebase not fully initialized, skipping auth listener setup.");
      // If Firebase failed to initialize, we should not attempt auth operations.
      // Set isAuthReady to true to prevent infinite loading state, but indicate no auth.
      setIsAuthReady(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in.
        const uid = user.uid;
        setFirestoreUserId(uid);
        setIsAuthReady(true);
        console.log("Firebase Auth: User signed in with UID:", uid);

        // Fetch username from userProfiles collection if available, otherwise use a fallback
        const userProfileDocRef = doc(db, `artifacts/${appId}/public/data/userProfiles/${uid}`);
        const userProfileDoc = await getDoc(userProfileDocRef);
        let usernameToUse = 'Guest'; // Default username

        if (userProfileDoc.exists()) {
          usernameToUse = userProfileDoc.data().username;
        } else if (user.displayName) {
          usernameToUse = user.displayName; // Use Google's display name if available
        } else if (user.email) {
          usernameToUse = user.email; // Fallback to email
        }

        // Set currentUser based on Firebase user and fetched username
        // Ensure that isOwner is correctly set for the hardcoded 'owner' user
        const storedUser = JSON.parse(localStorage.getItem('currentUser'));
        const isOwnerFromStorage = storedUser && storedUser.username === 'owner' && storedUser.isOwner;

        setCurrentUser({ username: usernameToUse, isOwner: isOwnerFromStorage, uid: uid });

        // Update user's presence in Firestore
        const userDocRef = doc(db, `artifacts/${appId}/public/data/onlineUsers/${uid}`);
        await setDoc(userDocRef, {
          lastSeen: serverTimestamp(),
          username: usernameToUse, // Use the fetched username for presence
        }, { merge: true });
        console.log("Firebase Presence: Updated lastSeen for user:", uid);

        // Periodically update lastSeen timestamp
        const presenceInterval = setInterval(async () => {
          if (auth.currentUser) { // Ensure user is still authenticated before updating presence
            await setDoc(userDocRef, { lastSeen: serverTimestamp() }, { merge: true });
            console.log("Firebase Presence: Periodic update for user:", uid);
          } else {
            console.log("Firebase Presence: User not authenticated, stopping periodic updates.");
            clearInterval(presenceInterval);
          }
        }, 15000); // Update every 15 seconds

        // Cleanup function for interval
        return () => clearInterval(presenceInterval);

      } else {
        // User is signed out from Firebase.
        console.log("Firebase Auth: User is signed out. No automatic anonymous sign-in.");
        setIsAuthReady(true); // Auth process attempted, ready to proceed
        // If Firebase user is null, check localStorage for a previously logged-in owner
        const storedUser = JSON.parse(localStorage.getItem('currentUser'));
        if (storedUser && storedUser.username === 'owner' && storedUser.isOwner) {
          setCurrentUser({ username: 'owner', isOwner: true, uid: 'owner-uid' }); // Re-set owner from localStorage
        } else {
          setCurrentUser(null); // Ensure currentUser is null if no Firebase user and not owner
        }
      }
    });

    return () => unsubscribe(); // Cleanup auth listener on unmount
  }, [app, auth, db, appId, firebaseInitialized]); // Added firebaseInitialized to dependencies

  // Effect to listen for real-time connected users from Firestore
  useEffect(() => {
    // Only proceed if Firebase was successfully initialized AND a user is authenticated (even anonymously)
    // This ensures that the listener only tries to fetch data if there's an active user session.
    if (!firebaseInitialized || !db || !isAuthReady || !auth.currentUser) {
      console.log("Firestore Listener: Firebase not fully initialized, Auth not ready, or no user authenticated. Skipping online users listener setup.");
      setConnectedListeners(0); // Reset count if no user is authenticated
      return;
    }

    const onlineUsersCollectionRef = collection(db, `artifacts/${appId}/public/data/onlineUsers`);
    const q = query(onlineUsersCollectionRef);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const now = Date.now();
      const activeThreshold = 30 * 1000; // 30 seconds
      let activeCount = 0;

      console.log("Firestore Listener: Snapshot received. Docs count:", snapshot.docs.length);
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Check if lastSeen is a Firestore Timestamp and convert it
        if (data.lastSeen && typeof data.lastSeen.toDate === 'function') {
          const lastSeenTime = data.lastSeen.toDate().getTime();
          if (now - lastSeenTime < activeThreshold) {
            activeCount++;
          }
        }
      });
      setConnectedListeners(activeCount);
      console.log("Firestore Listener: Active listeners updated to:", activeCount);
    }, (error) => {
      console.error("Firestore Listener: Error fetching online users:", error);
      // Removed modal message here, as it's handled by not attaching the listener if no user.
      // setModalMessage("Σφάλμα κατά τη φόρτωση των online χρηστών.");
    });

    return () => unsubscribe(); // Cleanup listener on unmount
  }, [db, isAuthReady, appId, firebaseInitialized, auth.currentUser]); // Added auth.currentUser to dependencies

  // Handler for successful login
  const handleLogin = (user) => {
    setCurrentUser(user);
    setShowAuthModal(false); // Close login modal after successful login
    setCurrentView('radio'); // Redirect to radio view after login
  };

  // Handler for explicit guest login
  const handleGuestLogin = async () => {
    if (!firebaseInitialized || !auth || !db) {
      setModalMessage('Η σύνδεση ως επισκέπτης δεν είναι διαθέσιμη. Παρακαλώ δοκιμάστε ξανά αργότερα.');
      return;
    }
    try {
      const userCredential = await signInAnonymously(auth);
      const user = userCredential.user;
      // For guest, username is 'Guest'
      handleLogin({ username: 'Guest', isOwner: false, uid: user.uid });
      setModalMessage('Συνδεθήκατε ως επισκέπτης!');
    } catch (error) {
      console.error("Firebase Auth Error (Guest Login):", error);
      setModalMessage("Αποτυχία σύνδεσης ως επισκέπτης. Παρακαλώ δοκιμάστε ξανά.");
    }
  };


  // Handler for logout
  const theHandleLogout = async () => { // Renamed to avoid conflict if any, though not strictly necessary
    try {
      // If the current user is a Firebase authenticated user (not the hardcoded owner)
      if (auth && auth.currentUser && auth.currentUser.uid !== 'owner-uid') { // Check if auth is defined
        // Mark user as inactive in Firestore before signing out
        const userDocRef = doc(db, `artifacts/${appId}/public/data/onlineUsers/${auth.currentUser.uid}`);
        await setDoc(userDocRef, { lastSeen: new Date(0) }, { merge: true });
        console.log("Firebase Presence: Marked user inactive on logout:", auth.currentUser.uid);
        await auth.signOut(); // Sign out from Firebase
        console.log("Firebase Auth: User signed out.");
      } else {
        console.log("Owner logout or anonymous/dummy user, no Firebase signOut needed.");
      }
      localStorage.removeItem('currentUser'); // Clear user from localStorage
      setCurrentUser(null); // Clear current user state
      setCurrentView('radio'); // Redirect to radio view after logout
    } catch (error) {
      console.error("Error during logout:", error);
      setModalMessage("Σφάλμα κατά την αποσύνδεση. Παρακαλώ δοκιμάστε ξανά.");
    }
  };

  // Handler for saving an article (add/edit)
  const handleSaveArticle = (article) => {
    if (!article.title || !article.content) {
      setModalMessage('Παρακαλώ συμπληρώστε τόσο τον τίτλο όσο και το περιεχόμενο.');
      return;
    }
    if (article.id) {
      setArticles(articles.map(art => (art.id === article.id ? article : art)));
    } else {
      setArticles([...articles, { ...article, id: Date.now() }]);
    }
    setEditingArticle(null);
    setCurrentArticleTitle(''); // Clear states after saving
    setCurrentArticleContent('');
    setCurrentArticleImageUrl(''); // Clear image URL after saving
    setCurrentView('articles');
  };

  // Handler for deleting an article
  const handleDeleteArticle = (id) => {
    setArticles(articles.filter(article => article.id !== id));
  };

  // Handler for adding a YouTube video
  const handleAddYoutubeVideo = (videoId, title) => {
    if (!videoId || !title) {
      setModalMessage('Παρακαλώ εισάγετε τόσο το αναγνωριστικό βίντεο όσο και τον τίτλο.');
      return;
    }
    setYoutubeVideos([...youtubeVideos, { id: Date.now(), videoId, title }]);
    setCurrentView('youtube');
  };

  // Handler for deleting a YouTube video
  const handleDeleteYoutubeVideo = (id) => {
    setYoutubeVideos(youtubeVideos.filter(video => video.id !== id));
  };

  // Article Form Component
  const ArticleForm = ({ article, onSave, onCancel, setModalMessage }) => {
    const [title, setTitle] = useState(article ? article.title : '');
    const [content, setContent] = useState(article ? article.content : '');
    const [imageUrl, setImageUrl] = useState(article ? article.imageUrl : '');
    // Removed imagePrompt state and related AI generation logic

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave({ ...article, title, content, imageUrl });
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Check file type
        if (!file.type.startsWith('image/')) {
          setModalMessage('Παρακαλώ επιλέξτε ένα αρχείο εικόνας (π.χ. JPG, PNG, GIF).');
          e.target.value = ''; // Clear the input
          setImageUrl('');
          return;
        }
        // Check file size (e.g., max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5 MB
        if (file.size > maxSize) {
          setModalMessage('Το μέγεθος της εικόνας υπερβαίνει το επιτρεπόμενο όριο (5MB).');
          e.target.value = ''; // Clear the input
          setImageUrl('');
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          setImageUrl(reader.result); // Set base64 string as imageUrl
          setModalMessage('Η εικόνα επιλέχθηκε επιτυχώς.');
        };
        reader.onerror = () => {
          setModalMessage('Αποτυχία ανάγνωσης του αρχείου εικόνας.');
          setImageUrl('');
        };
        reader.readAsDataURL(file);
      } else {
        setImageUrl('');
      }
    };

    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-lg max-w-2xl mx-auto">
        <h2 className="text-3xl font-bold text-purple-400 mb-6 text-center">
          {article ? 'Επεξεργασία Άρθρου' : 'Δημιουργία Νέου Άρθρου'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-purple-300 text-lg font-semibold mb-2">Τίτλος</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="Εισάγετε τίτλο άρθρου"
              required
            />
          </div>
          <div>
            <label htmlFor="content" className="block text-purple-300 text-lg font-semibold mb-2">Περιεχόμενο</label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows="10"
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none resize-y"
              placeholder="Γράψτε το περιεχόμενο του άρθρου σας εδώ..."
              required
            ></textarea>
          </div>
          <div>
            <label htmlFor="imageUpload" className="block text-purple-300 text-lg font-semibold mb-2">Μεταφόρτωση Φωτογραφίας</label>
            <input
              type="file"
              id="imageUpload"
              accept="image/*" // Accept only image files
              onChange={handleFileChange}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
            />
            {imageUrl && (
              <div className="mt-4">
                <p className="text-purple-300 mb-2">Προεπισκόπηση Φωτογραφίας:</p>
                <img src={imageUrl} alt="Προεπισκόπηση" className="max-w-full h-auto rounded-md border border-purple-600" />
              </div>
            )}
            <p className="text-sm text-gray-400 mt-1">
              Επιλέξτε μια φωτογραφία από τον υπολογιστή σας (μέγ. 5MB, μορφές εικόνας).
            </p>
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-red-600 text-white font-bold rounded-full shadow-lg hover:bg-red-700 transition duration-300 transform hover:scale-105"
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105"
            >
              Αποθήκευση Άρθρου
            </button>
          </div>
        </form>
      </div>
    );
  };

  // YouTube Form Component
  const YoutubeForm = ({ onSave, onCancel }) => {
    const [videoId, setVideoId] = useState('');
    const [title, setTitle] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(videoId, title);
      setVideoId('');
      setTitle('');
    };

    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-lg max-w-2xl mx-auto">
        <h2 className="text-3xl font-bold text-purple-400 mb-6 text-center">Προσθήκη Βίντεο YouTube</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="videoId" className="block text-purple-300 text-lg font-semibold mb-2">Αναγνωριστικό Βίντεο YouTube</label>
            <input
              type="text"
              id="videoId"
              value={videoId}
              onChange={(e) => setVideoId(e.target.value)}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="π.χ., dQw4w9WgXcQ"
              required
            />
            <p className="text-sm text-gray-400 mt-1">
              Βρείτε αυτό στη διεύθυνση URL του βίντεο YouTube μετά το "v=".
            </p>
          </div>
          <div>
            <label htmlFor="videoTitle" className="block text-purple-300 text-lg font-semibold mb-2">Τίτλος Βίντεο</label>
            <input
              type="text"
              id="videoTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="Εισάγετε έναν περιγραφικό τίτλο για το βίντεο"
              required
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-red-600 text-white font-bold rounded-full shadow-lg hover:bg-red-700 transition duration-300 transform hover:scale-105"
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105"
            >
              Προσθήκη Βίντεο
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Owner Dashboard for managing settings
  const OwnerDashboard = ({ radioStreamUrl, setRadioStreamUrl, onCancel, setModalMessage,
    currentPlayingSongTitle, setCurrentPlayingSongTitle,
    currentPlayingAlbumArtUrl, setCurrentPlayingAlbumArtUrl }) => {

    const [streamInput, setStreamInput] = useState(radioStreamUrl);
    const [songTitleInput, setSongTitleInput] = useState(currentPlayingSongTitle);
    const [albumArtUrlInput, setAlbumArtUrlInput] = useState(currentPlayingAlbumArtUrl);

    const handleSaveSettings = () => {
      setRadioStreamUrl(streamInput);
      setCurrentPlayingSongTitle(songTitleInput);
      setCurrentPlayingAlbumArtUrl(albumArtUrlInput);
      setModalMessage('Οι ρυθμίσεις ενημερώθηκαν επιτυχώς!');
    };

    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-lg max-w-2xl mx-auto">
        <h2 className="text-3xl font-bold text-purple-400 mb-6 text-center">Ρυθμίσεις Ιδιοκτήτη</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="radioStream" className="block text-purple-300 text-lg font-semibold mb-2">Διεύθυνση URL Ζωντανής Ραδιοφωνικής Ροής</label>
            <input
              type="url"
              id="radioStream"
              value={streamInput}
              onChange={(e) => setStreamInput(e.target.value)}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="π.χ., https://your-radio-stream.com/live.mp3"
            />
          </div>

          <div>
            <label htmlFor="songTitle" className="block text-purple-300 text-lg font-semibold mb-2">Τίτλος Τραγουδιού σε Αναπαραγωγή</label>
            <input
              type="text"
              id="songTitle"
              value={songTitleInput}
              onChange={(e) => setSongTitleInput(e.target.value)}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="π.χ., My Awesome Song"
            />
            <p className="text-sm text-gray-400 mt-1">
              Ο τίτλος που θα εμφανίζεται στον player.
            </p>
          </div>

          <div>
            <label htmlFor="albumArtUrl" className="block text-purple-300 text-lg font-semibold mb-2">URL Φωτογραφίας Άλμπουμ</label>
            <input
              type="url"
              id="albumArtUrl"
              value={albumArtUrlInput}
              onChange={(e) => setAlbumArtUrlInput(e.target.value)}
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-purple-600 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="π.χ., https://example.com/album.jpg"
            />
            <p className="text-sm text-gray-400 mt-1">
              Η URL για την εικόνα του εξωφύλλου του άλμπουμ.
            </p>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-red-600 text-white font-bold rounded-full shadow-lg hover:bg-red-700 transition duration-300 transform hover:scale-105"
            >
              Πίσω στον Πίνακα Ελέγχου
            </button>
            <button
              type="button"
              onClick={handleSaveSettings}
              className="px-6 py-3 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105"
            >
              Αποθήκευση Ρυθμίσεων
            </button>
          </div>
        </div>
      </div>
    );
  };


  // Render content based on current view
  const renderContent = () => {
    // Initial screen is radio, not login
    // Login modal appears only when needed
    switch (currentView) {
      case 'radio':
        return (
          // Use flexbox to place player and chat side-by-side
          // On small screens (sm:), they will stack (flex-col)
          // On medium and large screens (md:), they will be in a row (flex-row)
          <div className="flex flex-col md:flex-row items-start justify-center gap-8 min-h-[calc(100vh-80px)] p-4">
            {/* Left side: Radio Player */}
            <div className="bg-gray-800 p-8 rounded-xl shadow-2xl border-2 border-purple-700 w-full md:flex-1 md:max-w-md text-center flex-shrink-0 h-[500px] flex flex-col justify-between">
              <h2 className="text-4xl font-extrabold text-purple-400 mb-6 animate-pulse">
                BeHappyRadio Ζωντανή Ροή
              </h2>
              <p className="text-gray-300 text-lg mb-4">Συντονιστείτε στους καλύτερους ρυθμούς!</p>

              {/* Display real-time connected listeners */}
              <div className="text-purple-300 text-xl font-bold mb-6">
                {currentUser ? (
                  <>Ακροατές Online: <span className="text-green-400">{connectedListeners}</span></>
                ) : (
                  'Συνδεθείτε για να δείτε τους online ακροατές.'
                )}
              </div>

              {/* Main Iframe Player */}
              <div className="w-full mb-6 rounded-lg overflow-hidden border-2 border-purple-600 shadow-lg flex-grow flex items-center justify-center">
                <iframe
                  width="100%"
                  height="100%" // Make iframe fill its container
                  src="https://radio.webhosting4u.gr/AudioPlayer/behappyradio?mount=&"
                  frameBorder="0"
                  style={{ border: 0 }}
                  allow="autoplay"
                  title="BeHappyRadio Player"
                ></iframe>
              </div>

              {/* This block is displayed only if the user is an owner */}
              {isOwner && (
                <div className="mt-8 text-gray-400 text-sm">
                  
                </div>
              )}
            </div>
            {/* Right side: Chat Component */}
            <ChatComponent
              currentUser={currentUser}
              setModalMessage={setModalMessage}
              setShowAuthModal={setShowAuthModal}
              isOwner={isOwner} // Pass isOwner prop to ChatComponent
            />
          </div>
        );
      case 'articles':
        return (
          <div className="p-6">
            <h2 className="text-4xl font-bold text-purple-400 mb-8 text-center">Άρθρα</h2>
            {isOwner && ( // Display "Add New Article" button only if owner
              <div className="flex justify-center mb-6">
                <button
                  onClick={() => {
                    setEditingArticle(null); // For new article, editingArticle is null
                    setCurrentArticleTitle(''); // Clear title for new article
                    setCurrentArticleContent(''); // Clear content for new article
                    setCurrentArticleImageUrl(''); // Clear image URL for new article
                    setCurrentView('article-form');
                  }}
                  className="px-8 py-4 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105 flex items-center space-x-2"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  <span>Προσθήκη Νέου Άρθρου</span>
                </button>
              </div>
            )}
            {articles.length === 0 ? (
              <p className="text-gray-400 text-center text-xl mt-10">Δεν υπάρχουν άρθρα ακόμα. {isOwner && 'Ξεκινήστε προσθέτοντας ένα!'}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map(article => (
                  <div key={article.id} className="bg-gray-800 p-6 rounded-lg shadow-xl border border-purple-700 flex flex-col">
                    {article.imageUrl && (
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-48 object-cover rounded-md mb-4"
                        onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/400x200/6B46C1/FFFFFF?text=Image+Not+Found`; }}
                      />
                    )}
                    <h3 className="text-2xl font-semibold text-purple-300 mb-3">{article.title}</h3>
                    <p className="text-gray-300 text-base line-clamp-4 mb-4">{article.content}</p>
                    {isOwner && ( // Display edit/delete buttons only if owner
                      <div className="mt-auto flex justify-end space-x-3">
                        <button
                          onClick={() => {
                            setEditingArticle(article); // Set article for editing
                            setCurrentArticleTitle(article.title); // Load article title
                            setCurrentArticleContent(article.content); // Load article content
                            setCurrentArticleImageUrl(article.imageUrl || ''); // Load image URL
                            setCurrentView('article-form');
                          }}
                          className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition duration-200 transform hover:scale-105"
                          aria-label="Επεξεργασία άρθρου"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-3.646 3.646l-2.828 2.828-1.5 1.5V16h4.5l1.5-1.5 2.828-2.828-4.5-4.5z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteArticle(article.id)}
                          className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition duration-200 transform hover:scale-105"
                          aria-label="Διαγραφή άρθρου"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'youtube':
        return (
          <div className="p-6">
            <h2 className="text-4xl font-bold text-purple-400 mb-8 text-center">Βίντεο YouTube</h2>
            {isOwner && ( // Display "Add New Video" button only if owner
              <div className="flex justify-center mb-6">
                <button
                  onClick={() => setCurrentView('youtube-form')}
                  className="px-8 py-4 bg-purple-600 text-white font-bold rounded-full shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105 flex items-center space-x-2"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  <span>Προσθήκη Νέου Βίντεο</span>
                </button>
              </div>
            )}
            {youtubeVideos.length === 0 ? (
              <p className="text-gray-400 text-center text-xl mt-10">Δεν υπάρχουν βίντεο ακόμα. {isOwner && 'Προσθέστε περιεχόμενο YouTube!'}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {youtubeVideos.map(video => (
                  <div key={video.id} className="bg-gray-800 p-6 rounded-lg shadow-xl border border-purple-700 flex flex-col">
                    <h3 className="text-xl font-semibold text-purple-300 mb-3 line-clamp-2">{video.title}</h3>
                    <div className="relative w-full pb-[56.25%] mb-4 rounded-md overflow-hidden"> {/* 16:9 aspect ratio */}
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${video.videoId}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                    {isOwner && ( // Display delete button only if owner
                      <div className="mt-auto flex justify-end">
                        <button
                          onClick={() => handleDeleteYoutubeVideo(video.id)}
                          className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition duration-200 transform hover:scale-105"
                          aria-label="Διαγραφή βίντεο"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'dashboard':
        return (
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] p-4">
            <div className="bg-gray-800 p-8 rounded-xl shadow-2xl border-2 border-purple-700 w-full max-w-2xl text-center">
              <h2 className="text-4xl font-extrabold text-purple-400 mb-8">Πίνακας Ελέγχου Ιδιοκτήτη</h2>
              <p className="text-gray-300 text-lg mb-10">Διαχειριστείτε το περιεχόμενό σας από εδώ.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button
                  onClick={() => setCurrentView('articles')}
                  className="p-6 bg-purple-600 text-white font-bold rounded-xl shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105 flex flex-col items-center justify-center space-y-3"
                >
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 4H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zm-1 14H6a1 1 0 01-1-1V7a1 1 0 011-1h12a1 1 0 011 1v10a1 1 0 01-1 1zM8 9h8v2H8V9zm0 4h8v2H8v-2z" />
                  </svg>
                  <span className="text-xl">Διαχείριση Άρθρων ({articles.length})</span>
                </button>
                <button
                  onClick={() => setCurrentView('youtube')}
                  className="p-6 bg-purple-600 text-white font-bold rounded-xl shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105 flex flex-col items-center justify-center space-y-3"
                >
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.615 3.184c-1.142-.224-2.924-.373-5.49-.373-2.566 0-4.348.149-5.49.373C7.51 3.407 6.257 3.993 5.307 4.943c-.95.95-1.536 2.203-1.759 3.345-.224 1.142-.373 2.924-.373 5.49 0 2.566.149 4.348.373 5.49 1.142.224 2.924.373 5.49.373 2.566 0 4.348-.149 5.49-.373 1.142-.224 2.407-.81 3.357-1.76.95-.95 1.536-2.203 1.759-3.345.224-1.142-.373-2.924-.373 5.49 0-2.566-.149-4.348-.373-5.49-.224-1.142-.81-2.407-1.76-3.357-.95-.95-2.203-1.536-3.345-1.759zM9.999 15.606V8.394L15.405 12l-5.406 3.606z" />
                  </svg>
                  <span className="text-xl">Διαχείριση Βίντεο YouTube ({youtubeVideos.length})</span>
                </button>
                <button
                  onClick={() => setCurrentView('owner-settings')}
                  className="p-6 bg-purple-600 text-white font-bold rounded-xl shadow-lg hover:bg-purple-700 transition duration-300 transform hover:scale-105 flex flex-col items-center justify-center space-y-3"
                >
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 15.36l-4.243 2.449.707-4.899-3.536-3.536 4.9-.707L12 3l2.172 4.343 4.9.707-3.536 3.536.707 4.899L12 15.36zM12 18a6 6 0 100-12 6 6 0 000 12z" />
                  </svg>
                  <span className="text-xl">Ρυθμίσεις Ιδιοκτήτη</span>
                </button>
              </div>
            </div>
          </div>
        );
      case 'article-form':
        return (
          <ArticleForm
            article={editingArticle}
            onSave={handleSaveArticle}
            onCancel={() => {
              setEditingArticle(null);
              setCurrentArticleTitle(''); // Clear title on cancel
              setCurrentArticleContent(''); // Clear content on cancel
              setCurrentArticleImageUrl(''); // Clear image URL on cancel
              setCurrentView('articles');
            }}
            setModalMessage={setModalMessage} // Pass setModalMessage to ArticleForm
          />
        );
      case 'youtube-form':
        return (
          <YoutubeForm
            onSave={handleAddYoutubeVideo}
            onCancel={() => setCurrentView('youtube')}
          />
        );
      case 'owner-settings':
        return (
          <OwnerDashboard
            radioStreamUrl={radioStreamUrl}
            setRadioStreamUrl={setRadioStreamUrl}
            onCancel={() => setCurrentView('dashboard')}
            setModalMessage={setModalMessage}
            currentPlayingSongTitle={currentPlayingSongTitle}
            setCurrentPlayingSongTitle={setCurrentPlayingSongTitle}
            currentPlayingAlbumArtUrl={currentPlayingAlbumArtUrl}
            setCurrentPlayingAlbumArtUrl={setCurrentPlayingAlbumArtUrl}
          />
        );
      case 'about':
        return <AboutUsComponent />;
      case 'contact':
        return <ContactUsComponent setModalMessage={setModalMessage} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white font-inter">
      {/* Note: CDN links for Tailwind and Google Fonts have been removed from here.
          You should have them installed and configured in your project (e.g., in index.css). */}

      {/* Navigation Bar */}
      <nav className="bg-gray-800 p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex flex-wrap justify-between items-center">
          <div className="text-purple-400 text-3xl font-extrabold tracking-wider">
            <span className="animate-pulse" onClick={() => setCurrentView('radio')}>BeHappyRadio</span>
          </div>
          {/* Navigation buttons are always visible */}
          <div className="flex space-x-4 mt-4 md:mt-0 flex-wrap justify-center">
            <button
              onClick={() => setCurrentView('radio')}
              className={`px-5 py-2 rounded-full font-semibold transition duration-300 ${
                currentView === 'radio' ? 'bg-purple-700 text-white shadow-md' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Ραδιόφωνο
            </button>
            <button
              onClick={() => setCurrentView('articles')}
              className={`px-5 py-2 rounded-full font-semibold transition duration-300 ${
                currentView === 'articles' || currentView === 'article-form' ? 'bg-purple-700 text-white shadow-md' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Άρθρα
            </button>
            <button
              onClick={() => setCurrentView('youtube')}
              className={`px-5 py-2 rounded-full font-semibold transition duration-300 ${
                currentView === 'youtube' || currentView === 'youtube-form' ? 'bg-purple-700 text-white shadow-md' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Βίντεο
            </button>
            <button
              onClick={() => setCurrentView('about')}
              className={`px-5 py-2 rounded-full font-semibold transition duration-300 ${
                currentView === 'about' ? 'bg-purple-700 text-white shadow-md' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Σχετικά
            </button>
            
            {isOwner && ( // Display "Dashboard" button only if owner
              <button
                onClick={() => setCurrentView('dashboard')}
                className={`px-5 py-2 rounded-full font-semibold transition duration-300 ${
                  currentView === 'dashboard' || currentView === 'owner-settings' ? 'bg-purple-700 text-white shadow-md' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Πίνακας Ελέγχου
              </button>
            )}
          </div>
          {/* Display username and logout button if logged in */}
          {isLoggedIn ? (
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <span className="text-gray-300 text-lg">Γεια σου, <span className="font-bold text-purple-300">{currentUser.username}</span>!</span>
              <button
                onClick={theHandleLogout} // Using the renamed handler
                className="px-5 py-2 rounded-full font-semibold bg-red-600 text-white shadow-md hover:bg-red-700 transition duration-300"
              >
                Αποσύνδεση
              </button>
            </div>
          ) : (
            // Display login/register button if not logged in
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-5 py-2 rounded-full font-semibold bg-purple-600 text-white shadow-md hover:bg-purple-700 transition duration-300"
              >
                Σύνδεση / Εγγραφή
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="container mx-auto py-8">
        {renderContent()}
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 p-4 text-center text-gray-400 mt-10">
        <p>&copy; {new Date().getFullYear()} BeHappyRadio. Με επιφύλαξη παντός δικαιώματος.</p>
      </footer>

      {/* Message Modal */}
      <MessageModal message={modalMessage} onClose={() => setModalMessage('')} />

      {/* Login/Registration Modal (displayed only when showAuthModal is true) */}
      {showAuthModal && (
        <AuthComponent
          onLogin={handleLogin}
          setModalMessage={setModalMessage}
          onClose={() => setShowAuthModal(false)}
          onGuestLogin={handleGuestLogin} // Pass the new guest login handler
        />
      )}
    </div>
  );
};

export default App;
